import { getSupabaseClient } from './supabase';

export class NotificationService {
  private static instance: NotificationService;
  private lastLoginTime: Date | null = null;

  private constructor() {}

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  // Track user login
  async trackUserLogin(userEmail: string): Promise<void> {
    try {
      const supabase = await getSupabaseClient(true);
      
      // Insert new login record
      await (supabase as any)
        .from('user_login_history')
        .insert({
          user_email: userEmail,
          login_time: new Date().toISOString()
        });

      // Get last login time before this one
      const { data: lastLogin } = await (supabase as any)
        .from('user_login_history')
        .select('login_time')
        .eq('user_email', userEmail)
        .order('login_time', { ascending: false })
        .limit(2);

      if (lastLogin && lastLogin.length > 1) {
        this.lastLoginTime = new Date(lastLogin[1].login_time);
      }
    } catch (error) {
      console.error('Error tracking user login:', error);
    }
  }

  // Check if we should show welcome back modal
  async shouldShowWelcomeBack(userEmail: string): Promise<boolean> {
    try {
      const supabase = await getSupabaseClient(true);
      
      // Get user's last two logins
      const { data: logins } = await (supabase as any)
        .from('user_login_history')
        .select('login_time')
        .eq('user_email', userEmail)
        .order('login_time', { ascending: false })
        .limit(2);

      if (!logins || logins.length < 2) {
        // First login or no previous login
        return true;
      }

      // Check time difference between last two logins
      const currentLogin = new Date(logins[0].login_time);
      const previousLogin = new Date(logins[1].login_time);
      const hoursDifference = (currentLogin.getTime() - previousLogin.getTime()) / (1000 * 60 * 60);

      // Show modal if more than 24 hours since last login
      return hoursDifference >= 24;
    } catch (error) {
      console.error('Error checking welcome back status:', error);
      return false;
    }
  }

  // Get count of unseen notifications
  async getUnseenNotificationCount(userEmail: string): Promise<number> {
    try {
      const supabase = await getSupabaseClient(true);
      const today = new Date().toISOString().split('T')[0];
      
      // Get active notifications
      const { data: notifications } = await (supabase as any)
        .from('user_notifications')
        .select('id')
        .eq('is_active', true)
        .lte('show_after_date', today)
        .or(`expires_date.is.null,expires_date.gte.${today}`);

      if (!notifications || notifications.length === 0) return 0;

      // Get dismissed notifications
      const { data: dismissals } = await (supabase as any)
        .from('user_notification_dismissals')
        .select('notification_id')
        .eq('user_email', userEmail);

      const dismissedIds = new Set(dismissals?.map((d: any) => d.notification_id) || []);
      const unseenCount = notifications.filter((n: any) => !dismissedIds.has(n.id)).length;

      return unseenCount;
    } catch (error) {
      console.error('Error getting unseen notification count:', error);
      return 0;
    }
  }

  // Track user logout
  async trackUserLogout(userEmail: string): Promise<void> {
    try {
      const supabase = await getSupabaseClient(true);
      
      // Update the most recent login record with logout time
      const { data: lastLogin } = await (supabase as any)
        .from('user_login_history')
        .select('id')
        .eq('user_email', userEmail)
        .order('login_time', { ascending: false })
        .limit(1)
        .single();

      if (lastLogin) {
        await (supabase as any)
          .from('user_login_history')
          .update({ logout_time: new Date().toISOString() })
          .eq('id', lastLogin.id);
      }
    } catch (error) {
      console.error('Error tracking user logout:', error);
    }
  }

  // Get personalized stats for welcome message
  async getPersonalizedStats(userEmail: string): Promise<{
    keywordsImproved?: number;
    contentUpdates?: number;
    teamActivity?: string[];
  }> {
    try {
      const supabase = await getSupabaseClient(true);
      const stats: any = {};

      // This could be expanded to fetch actual stats
      // For now, returning empty object
      return stats;
    } catch (error) {
      console.error('Error getting personalized stats:', error);
      return {};
    }
  }
}

export const notificationService = NotificationService.getInstance();